// Mathematics Topics M06-M23 Data Structure
// Systematic organization for Supabase migration

export const mathTopicsM06M23 = {
  // M06. 變換與對稱 / Transformation & Symmetry
  M06: {
    id: 'M06',
    title: '變換與對稱',
    titleEn: 'Transformation & Symmetry',
    description: '學習幾何變換包括平移、旋轉、反射和放大縮小，以及對稱性質',
    icon: '🔄',
    modules: {
      M06_01: {
        id: 'M06_01',
        title: '平移變換',
        titleEn: 'Translation',
        description: '物體在平面上的位移變換',
        examples: [
          {
            id: 'M06_01_01',
            question: '點A(3, 2)經過向量(4, -1)平移後的座標是？',
            answer: '(7, 1)',
            solution: '平移後座標 = 原座標 + 平移向量 = (3+4, 2+(-1)) = (7, 1)',
            difficulty: 'basic'
          },
          {
            id: 'M06_01_02',
            question: '三角形ABC的頂點A(1,2), B(3,4), C(5,1)，經過向量(-2,3)平移後，求新三角形的頂點座標',
            answer: "A'(-1,5), B'(1,7), C'(3,4)",
            solution: "A' = (1-2, 2+3) = (-1,5), B' = (3-2, 4+3) = (1,7), C' = (5-2, 1+3) = (3,4)",
            difficulty: 'intermediate'
          },
          {
            id: 'M06_01_03',
            question: '直線y = 2x + 1經過向量(3, -2)平移後的方程式是？',
            answer: 'y = 2x - 7',
            solution: '設新直線上一點(x, y)，原直線上對應點為(x-3, y+2)，代入原方程：y+2 = 2(x-3) + 1，得y = 2x - 7',
            difficulty: 'advanced'
          },
          {
            id: 'M06_01_04',
            question: '圓(x-2)² + (y-3)² = 4經過向量(1, -2)平移後的方程式是？',
            answer: '(x-3)² + (y-1)² = 4',
            solution: '圓心(2,3)平移後變為(2+1, 3-2) = (3,1)，半徑不變，所以新方程為(x-3)² + (y-1)² = 4',
            difficulty: 'intermediate'
          },
          {
            id: 'M06_01_05',
            question: '拋物線y = x²經過向量(h, k)平移後變為y = (x-2)² + 3，求h和k的值',
            answer: 'h = 2, k = 3',
            solution: '拋物線y = x²的頂點為(0,0)，平移後頂點為(2,3)，所以平移向量為(2,3)',
            difficulty: 'advanced'
          }
        ]
      },
      M06_02: {
        id: 'M06_02',
        title: '旋轉變換',
        titleEn: 'Rotation',
        description: '物體繞固定點的旋轉變換',
        examples: [
          {
            id: 'M06_02_01',
            question: '點P(4, 0)繞原點逆時針旋轉90°後的座標是？',
            answer: '(0, 4)',
            solution: '繞原點逆時針旋轉90°：(x, y) → (-y, x)，所以(4, 0) → (0, 4)',
            difficulty: 'basic'
          },
          {
            id: 'M06_02_02',
            question: '點Q(3, 4)繞原點順時針旋轉90°後的座標是？',
            answer: '(4, -3)',
            solution: '繞原點順時針旋轉90°：(x, y) → (y, -x)，所以(3, 4) → (4, -3)',
            difficulty: 'basic'
          },
          {
            id: 'M06_02_03',
            question: '點R(2, 1)繞原點旋轉180°後的座標是？',
            answer: '(-2, -1)',
            solution: '繞原點旋轉180°：(x, y) → (-x, -y)，所以(2, 1) → (-2, -1)',
            difficulty: 'basic'
          },
          {
            id: 'M06_02_04',
            question: '點S(1, 2)繞點(3, 1)逆時針旋轉90°後的座標是？',
            answer: '(2, 3)',
            solution: '先平移使旋轉中心到原點：(1-3, 2-1) = (-2, 1)，旋轉90°：(-1, -2)，再平移回去：(-1+3, -2+1) = (2, -1)',
            difficulty: 'advanced'
          },
          {
            id: 'M06_02_05',
            question: '三角形頂點A(0,0), B(3,0), C(0,4)繞原點逆時針旋轉90°後的新頂點座標是？',
            answer: "A'(0,0), B'(0,3), C'(-4,0)",
            solution: "A(0,0) → (0,0), B(3,0) → (0,3), C(0,4) → (-4,0)",
            difficulty: 'intermediate'
          }
        ]
      },
      M06_03: {
        id: 'M06_03',
        title: '反射變換',
        titleEn: 'Reflection',
        description: '物體關於直線或點的反射變換',
        examples: [
          {
            id: 'M06_03_01',
            question: '點A(3, 2)關於x軸的反射像是？',
            answer: '(3, -2)',
            solution: '關於x軸反射：(x, y) → (x, -y)，所以(3, 2) → (3, -2)',
            difficulty: 'basic'
          },
          {
            id: 'M06_03_02',
            question: '點B(-1, 4)關於y軸的反射像是？',
            answer: '(1, 4)',
            solution: '關於y軸反射：(x, y) → (-x, y)，所以(-1, 4) → (1, 4)',
            difficulty: 'basic'
          },
          {
            id: 'M06_03_03',
            question: '點C(2, 3)關於直線y = x的反射像是？',
            answer: '(3, 2)',
            solution: '關於y = x反射：(x, y) → (y, x)，所以(2, 3) → (3, 2)',
            difficulty: 'intermediate'
          },
          {
            id: 'M06_03_04',
            question: '點D(1, 5)關於原點的反射像是？',
            answer: '(-1, -5)',
            solution: '關於原點反射：(x, y) → (-x, -y)，所以(1, 5) → (-1, -5)',
            difficulty: 'basic'
          },
          {
            id: 'M06_03_05',
            question: '直線y = 2x + 1關於y軸的反射像方程式是？',
            answer: 'y = -2x + 1',
            solution: '設反射像上一點(x, y)，原直線上對應點(-x, y)，代入原方程：y = 2(-x) + 1 = -2x + 1',
            difficulty: 'advanced'
          }
        ]
      },
      M06_04: {
        id: 'M06_04',
        title: '放大縮小',
        titleEn: 'Enlargement',
        description: '物體的放大和縮小變換',
        examples: [
          {
            id: 'M06_04_01',
            question: '點P(2, 3)以原點為中心放大2倍後的座標是？',
            answer: '(4, 6)',
            solution: '以原點為中心放大k倍：(x, y) → (kx, ky)，所以(2, 3) → (2×2, 3×2) = (4, 6)',
            difficulty: 'basic'
          },
          {
            id: 'M06_04_02',
            question: '三角形頂點A(1,1), B(3,1), C(2,3)以原點為中心放大3倍後的新頂點座標是？',
            answer: "A'(3,3), B'(9,3), C'(6,9)",
            solution: "A(1,1) → (3,3), B(3,1) → (9,3), C(2,3) → (6,9)",
            difficulty: 'intermediate'
          },
          {
            id: 'M06_04_03',
            question: '點Q(6, 4)以原點為中心縮小1/2後的座標是？',
            answer: '(3, 2)',
            solution: '縮小1/2即放大1/2倍：(6, 4) → (6×1/2, 4×1/2) = (3, 2)',
            difficulty: 'basic'
          },
          {
            id: 'M06_04_04',
            question: '點R(1, 2)以點(2, 1)為中心放大3倍後的座標是？',
            answer: '(-1, 4)',
            solution: '先平移使中心到原點：(1-2, 2-1) = (-1, 1)，放大3倍：(-3, 3)，再平移回去：(-3+2, 3+1) = (-1, 4)',
            difficulty: 'advanced'
          },
          {
            id: 'M06_04_05',
            question: '圓x² + y² = 4以原點為中心放大2倍後的方程式是？',
            answer: 'x² + y² = 16',
            solution: '原圓半徑為2，放大2倍後半徑為4，所以新方程為x² + y² = 16',
            difficulty: 'intermediate'
          }
        ]
      },
      M06_05: {
        id: 'M06_05',
        title: '對稱性質',
        titleEn: 'Symmetry Properties',
        description: '軸對稱和點對稱的性質',
        examples: [
          {
            id: 'M06_05_01',
            question: '正方形有幾條對稱軸？',
            answer: '4條',
            solution: '正方形有4條對稱軸：兩條對角線和兩條邊的中垂線',
            difficulty: 'basic'
          },
          {
            id: 'M06_05_02',
            question: '等腰三角形有幾條對稱軸？',
            answer: '1條',
            solution: '等腰三角形有1條對稱軸：底邊的中垂線',
            difficulty: 'basic'
          },
          {
            id: 'M06_05_03',
            question: '正六邊形有幾條對稱軸？',
            answer: '6條',
            solution: '正六邊形有6條對稱軸：3條通過相對頂點，3條通過相對邊的中點',
            difficulty: 'intermediate'
          },
          {
            id: 'M06_05_04',
            question: '字母"H"有什麼對稱性質？',
            answer: '有水平和垂直對稱軸',
            solution: '字母H既有水平對稱軸（通過中間橫線），也有垂直對稱軸（通過中央）',
            difficulty: 'basic'
          },
          {
            id: 'M06_05_05',
            question: '拋物線y = x²關於哪條直線對稱？',
            answer: 'y軸（x = 0）',
            solution: '拋物線y = x²的對稱軸是y軸，因為f(-x) = (-x)² = x² = f(x)',
            difficulty: 'intermediate'
          }
        ]
      }
    }
  },

  // M07. 指數、指數函數與對數函數 / Indices, Exponential and logarithmic functions
  M07: {
    id: 'M07',
    title: '指數、指數函數與對數函數',
    titleEn: 'Indices, Exponential and logarithmic functions',
    description: '學習指數定律、指數函數、對數函數及其應用',
    icon: '📈',
    modules: {
      M07_01: {
        id: 'M07_01',
        title: '指數定律',
        titleEn: 'Laws of Indices',
        description: '指數運算的基本定律',
        examples: [
          {
            id: 'M07_01_01',
            question: '計算 2³ × 2⁴',
            answer: '2⁷ = 128',
            solution: '同底數相乘，指數相加：2³ × 2⁴ = 2³⁺⁴ = 2⁷ = 128',
            difficulty: 'basic'
          },
          {
            id: 'M07_01_02',
            question: '計算 3⁵ ÷ 3²',
            answer: '3³ = 27',
            solution: '同底數相除，指數相減：3⁵ ÷ 3² = 3⁵⁻² = 3³ = 27',
            difficulty: 'basic'
          },
          {
            id: 'M07_01_03',
            question: '計算 (2³)⁴',
            answer: '2¹² = 4096',
            solution: '冪的冪，指數相乘：(2³)⁴ = 2³ˣ⁴ = 2¹² = 4096',
            difficulty: 'basic'
          },
          {
            id: 'M07_01_04',
            question: '化簡 (x²y³)⁴',
            answer: 'x⁸y¹²',
            solution: '(x²y³)⁴ = (x²)⁴(y³)⁴ = x⁸y¹²',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_01_05',
            question: '計算 5⁻² × 5⁴',
            answer: '5² = 25',
            solution: '5⁻² × 5⁴ = 5⁻²⁺⁴ = 5² = 25',
            difficulty: 'intermediate'
          }
        ]
      },
      M07_02: {
        id: 'M07_02',
        title: '指數函數',
        titleEn: 'Exponential Functions',
        description: '指數函數的性質和圖像',
        examples: [
          {
            id: 'M07_02_01',
            question: '函數 f(x) = 2ˣ 在 x = 0 時的值是？',
            answer: '1',
            solution: 'f(0) = 2⁰ = 1',
            difficulty: 'basic'
          },
          {
            id: 'M07_02_02',
            question: '函數 g(x) = 3ˣ 在 x = -1 時的值是？',
            answer: '1/3',
            solution: 'g(-1) = 3⁻¹ = 1/3',
            difficulty: 'basic'
          },
          {
            id: 'M07_02_03',
            question: '指數函數 y = aˣ (a > 1) 的圖像性質是？',
            answer: '遞增函數，通過點(0,1)，水平漸近線y = 0',
            solution: '當a > 1時，y = aˣ是遞增函數，恆通過點(0,1)，x軸是水平漸近線',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_02_04',
            question: '解方程 2ˣ = 8',
            answer: 'x = 3',
            solution: '2ˣ = 8 = 2³，所以 x = 3',
            difficulty: 'basic'
          },
          {
            id: 'M07_02_05',
            question: '函數 h(x) = (1/2)ˣ 是遞增還是遞減函數？',
            answer: '遞減函數',
            solution: '因為底數1/2 < 1，所以h(x) = (1/2)ˣ是遞減函數',
            difficulty: 'intermediate'
          }
        ]
      },
      M07_03: {
        id: 'M07_03',
        title: '對數函數',
        titleEn: 'Logarithmic Functions',
        description: '對數函數的定義和性質',
        examples: [
          {
            id: 'M07_03_01',
            question: '計算 log₂ 8',
            answer: '3',
            solution: 'log₂ 8 = 3，因為 2³ = 8',
            difficulty: 'basic'
          },
          {
            id: 'M07_03_02',
            question: '計算 log₁₀ 100',
            answer: '2',
            solution: 'log₁₀ 100 = 2，因為 10² = 100',
            difficulty: 'basic'
          },
          {
            id: 'M07_03_03',
            question: '計算 log₅ 1',
            answer: '0',
            solution: 'log₅ 1 = 0，因為 5⁰ = 1',
            difficulty: 'basic'
          },
          {
            id: 'M07_03_04',
            question: '計算 log₃ (1/9)',
            answer: '-2',
            solution: 'log₃ (1/9) = log₃ 3⁻² = -2',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_03_05',
            question: '解方程 log₂ x = 5',
            answer: 'x = 32',
            solution: 'log₂ x = 5 表示 2⁵ = x，所以 x = 32',
            difficulty: 'basic'
          }
        ]
      },
      M07_04: {
        id: 'M07_04',
        title: '對數定律',
        titleEn: 'Laws of Logarithms',
        description: '對數運算的基本定律',
        examples: [
          {
            id: 'M07_04_01',
            question: '計算 log₂ 4 + log₂ 8',
            answer: '5',
            solution: 'log₂ 4 + log₂ 8 = log₂ (4 × 8) = log₂ 32 = 5',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_04_02',
            question: '計算 log₁₀ 100 - log₁₀ 10',
            answer: '1',
            solution: 'log₁₀ 100 - log₁₀ 10 = log₁₀ (100 ÷ 10) = log₁₀ 10 = 1',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_04_03',
            question: '計算 3 log₂ 4',
            answer: '6',
            solution: '3 log₂ 4 = log₂ 4³ = log₂ 64 = 6',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_04_04',
            question: '化簡 log₅ 25 + log₅ 5',
            answer: '3',
            solution: 'log₅ 25 + log₅ 5 = log₅ (25 × 5) = log₅ 125 = log₅ 5³ = 3',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_04_05',
            question: '計算 log₃ 27 - log₃ 9',
            answer: '1',
            solution: 'log₃ 27 - log₃ 9 = log₃ (27 ÷ 9) = log₃ 3 = 1',
            difficulty: 'intermediate'
          }
        ]
      },
      M07_05: {
        id: 'M07_05',
        title: '指數和對數的應用',
        titleEn: 'Applications of Exponential and Logarithmic Functions',
        description: '指數和對數在實際問題中的應用',
        examples: [
          {
            id: 'M07_05_01',
            question: '某細菌每小時增長一倍，初始有100個，3小時後有多少個？',
            answer: '800個',
            solution: '3小時後數量 = 100 × 2³ = 100 × 8 = 800個',
            difficulty: 'basic'
          },
          {
            id: 'M07_05_02',
            question: '某物質的半衰期是5年，初始質量100g，10年後剩餘多少？',
            answer: '25g',
            solution: '10年 = 2個半衰期，剩餘質量 = 100 × (1/2)² = 100 × 1/4 = 25g',
            difficulty: 'intermediate'
          },
          {
            id: 'M07_05_03',
            question: '年利率8%複利計算，本金1000元，多少年後變成2000元？',
            answer: '約9年',
            solution: '1000(1.08)ᵗ = 2000，(1.08)ᵗ = 2，t = log₁.₀₈ 2 ≈ 9年',
            difficulty: 'advanced'
          },
          {
            id: 'M07_05_04',
            question: '聲音強度的分貝數公式為 dB = 10 log₁₀(I/I₀)，若I = 100I₀，求分貝數',
            answer: '20 dB',
            solution: 'dB = 10 log₁₀(100I₀/I₀) = 10 log₁₀ 100 = 10 × 2 = 20 dB',
            difficulty: 'advanced'
          },
          {
            id: 'M07_05_05',
            question: '某城市人口按年率3%增長，現有人口50萬，多少年後達到100萬？',
            answer: '約23年',
            solution: '50(1.03)ᵗ = 100，(1.03)ᵗ = 2，t = log₁.₀₃ 2 ≈ 23年',
            difficulty: 'advanced'
          }
        ]
      }
    }
  },

  // M08. 二次方程與二次函數圖像 / Quadratic equations & Quadratic graphs
  M08: {
    id: 'M08',
    title: '二次方程與二次函數圖像',
    titleEn: 'Quadratic equations & Quadratic graphs',
    description: '學習二次方程的解法和二次函數的圖像性質',
    icon: '📊',
    modules: {
      M08_01: {
        id: 'M08_01',
        title: '二次方程解法',
        titleEn: 'Solving Quadratic Equations',
        description: '因式分解、配方法、求根公式',
        examples: [
          {
            id: 'M08_01_01',
            question: '解方程 x² - 5x + 6 = 0',
            answer: 'x = 2 或 x = 3',
            solution: '因式分解：(x - 2)(x - 3) = 0，所以 x = 2 或 x = 3',
            difficulty: 'basic'
          },
          {
            id: 'M08_01_02',
            question: '解方程 x² - 4x + 4 = 0',
            answer: 'x = 2（重根）',
            solution: '因式分解：(x - 2)² = 0，所以 x = 2（重根）',
            difficulty: 'basic'
          },
          {
            id: 'M08_01_03',
            question: '用配方法解 x² + 6x + 5 = 0',
            answer: 'x = -1 或 x = -5',
            solution: 'x² + 6x + 5 = (x + 3)² - 9 + 5 = (x + 3)² - 4 = 0，(x + 3)² = 4，x + 3 = ±2，x = -1 或 x = -5',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_01_04',
            question: '用求根公式解 2x² - 3x - 1 = 0',
            answer: 'x = (3 ± √17)/4',
            solution: 'a = 2, b = -3, c = -1，x = (3 ± √(9 + 8))/4 = (3 ± √17)/4',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_01_05',
            question: '判別式 b² - 4ac = 0 表示什麼？',
            answer: '方程有一個重根',
            solution: '當判別式 = 0時，二次方程有一個重根（兩個相等的實根）',
            difficulty: 'basic'
          }
        ]
      },
      M08_02: {
        id: 'M08_02',
        title: '二次函數圖像',
        titleEn: 'Quadratic Function Graphs',
        description: '二次函數的圖像性質和變換',
        examples: [
          {
            id: 'M08_02_01',
            question: '函數 y = x² 的頂點座標是？',
            answer: '(0, 0)',
            solution: '標準形式 y = x² 的頂點在原點 (0, 0)',
            difficulty: 'basic'
          },
          {
            id: 'M08_02_02',
            question: '函數 y = (x - 2)² + 3 的頂點座標是？',
            answer: '(2, 3)',
            solution: '頂點形式 y = (x - h)² + k 的頂點為 (h, k)，所以頂點是 (2, 3)',
            difficulty: 'basic'
          },
          {
            id: 'M08_02_03',
            question: '函數 y = -x² + 4x - 3 的對稱軸是？',
            answer: 'x = 2',
            solution: '對稱軸 x = -b/(2a) = -4/(2×(-1)) = 2',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_02_04',
            question: '函數 y = x² - 4x + 3 與x軸的交點是？',
            answer: '(1, 0) 和 (3, 0)',
            solution: '令 y = 0：x² - 4x + 3 = 0，(x - 1)(x - 3) = 0，x = 1 或 x = 3',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_02_05',
            question: '函數 y = 2x² 與 y = x² 的圖像有什麼不同？',
            answer: 'y = 2x² 更陡峭，開口更窄',
            solution: '係數2 > 1，使拋物線變得更陡峭，開口更窄',
            difficulty: 'basic'
          }
        ]
      },
      M08_03: {
        id: 'M08_03',
        title: '二次函數的最值',
        titleEn: 'Maximum and Minimum Values',
        description: '二次函數的最大值和最小值',
        examples: [
          {
            id: 'M08_03_01',
            question: '函數 y = x² - 4x + 5 的最小值是？',
            answer: '1',
            solution: '配方：y = (x - 2)² + 1，頂點 (2, 1)，開口向上，最小值為 1',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_03_02',
            question: '函數 y = -x² + 6x - 8 的最大值是？',
            answer: '1',
            solution: '配方：y = -(x - 3)² + 1，頂點 (3, 1)，開口向下，最大值為 1',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_03_03',
            question: '在區間 [0, 4] 上，函數 y = x² - 2x + 3 的最小值是？',
            answer: '2',
            solution: '頂點 (1, 2) 在區間內，且開口向上，所以最小值為 2',
            difficulty: 'advanced'
          },
          {
            id: 'M08_03_04',
            question: '矩形的周長為20，面積最大時的邊長是？',
            answer: '5',
            solution: '設一邊長為x，另一邊為10-x，面積 S = x(10-x) = -x² + 10x，最大值在 x = 5',
            difficulty: 'advanced'
          },
          {
            id: 'M08_03_05',
            question: '函數 y = 2x² - 8x + 9 在 x = 多少時取得最小值？',
            answer: 'x = 2',
            solution: '對稱軸 x = -b/(2a) = -(-8)/(2×2) = 2',
            difficulty: 'intermediate'
          }
        ]
      },
      M08_04: {
        id: 'M08_04',
        title: '二次不等式',
        titleEn: 'Quadratic Inequalities',
        description: '二次不等式的解法',
        examples: [
          {
            id: 'M08_04_01',
            question: '解不等式 x² - 5x + 6 > 0',
            answer: 'x < 2 或 x > 3',
            solution: '因式分解：(x - 2)(x - 3) > 0，畫數線得 x < 2 或 x > 3',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_04_02',
            question: '解不等式 x² - 4x + 3 ≤ 0',
            answer: '1 ≤ x ≤ 3',
            solution: '因式分解：(x - 1)(x - 3) ≤ 0，畫數線得 1 ≤ x ≤ 3',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_04_03',
            question: '解不等式 -x² + 2x + 3 > 0',
            answer: '-1 < x < 3',
            solution: '重寫為 x² - 2x - 3 < 0，因式分解：(x + 1)(x - 3) < 0，得 -1 < x < 3',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_04_04',
            question: '解不等式 x² + 4x + 4 ≥ 0',
            answer: '所有實數',
            solution: '因式分解：(x + 2)² ≥ 0，完全平方恆非負，解為所有實數',
            difficulty: 'basic'
          },
          {
            id: 'M08_04_05',
            question: '解不等式 x² + x + 1 > 0',
            answer: '所有實數',
            solution: '判別式 = 1 - 4 = -3 < 0，開口向上，恆為正，解為所有實數',
            difficulty: 'advanced'
          }
        ]
      },
      M08_05: {
        id: 'M08_05',
        title: '二次函數應用',
        titleEn: 'Applications of Quadratic Functions',
        description: '二次函數在實際問題中的應用',
        examples: [
          {
            id: 'M08_05_01',
            question: '物體從高度h = 80m處自由落下，高度公式為 h = 80 - 5t²，多少秒後落地？',
            answer: '4秒',
            solution: '落地時 h = 0：80 - 5t² = 0，t² = 16，t = 4秒',
            difficulty: 'basic'
          },
          {
            id: 'M08_05_02',
            question: '拋物線運動中，高度 h = -5t² + 20t + 1，最高點的高度是？',
            answer: '21m',
            solution: '最高點在 t = -20/(2×(-5)) = 2秒，h = -5(4) + 20(2) + 1 = 21m',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_05_03',
            question: '某商品定價x元，銷量為 (100 - x) 件，收入最大時的定價是？',
            answer: '50元',
            solution: '收入 R = x(100 - x) = -x² + 100x，最大值在 x = 50',
            difficulty: 'intermediate'
          },
          {
            id: 'M08_05_04',
            question: '用48m圍欄圍成矩形，面積最大時的長和寬是？',
            answer: '長12m，寬12m',
            solution: '設寬為x，長為24-x，面積 S = x(24-x) = -x² + 24x，最大值在 x = 12',
            difficulty: 'advanced'
          },
          {
            id: 'M08_05_05',
            question: '橋拱為拋物線形，跨度20m，最高點5m，距離中心5m處的高度是？',
            answer: '3.75m',
            solution: '以中心為原點，方程為 y = -5x²/100 + 5，x = 5時，y = -5(25)/100 + 5 = 3.75m',
            difficulty: 'advanced'
          }
        ]
      }
    }
  }
};

// Continue with remaining topics M09-M23...
// This is a partial implementation showing the structure
// Each topic follows the same pattern with modules and examples 